import * as THREE from "three";
import { POLYGONS, VARS  } from "../Global/variables";
import { deselectPanels, selectSinglePanel } from "../Panels/PanelFunctions";
import { selectSegment } from "../Segments/Functions";
import { deselectObstacle, selectObstacle } from "../Obstacles/ObstacleFunctions";
import { createSegmentPlane } from "../Global/functions";

// Main selection function triggered by a mouse click or similar interaction
export function selection(
  selectedIndices,
  setSelectedIndices,
  setPolygonDetails,
  mouse,
  sceneManager,
  setShowPanelSettings,
  setSolarPanel,
  polygonState,
  appendPanelSelection = false
) {
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, sceneManager.camera);

  // 🔒 1) If a vertex drag is ongoing, don’t change selection at all
  if (VARS.isVertexDragActive) {
    return;
  }

  const isInteractiveHandle = (object) => {
    if (!object) return false;
    const target = object.isGroup ? object : object.parent?.isGroup ? object.parent : object;
    const userData = target?.userData || object.userData || {};
    const isHandle =
      object.name === "vertex" ||
      target?.name === "vertex" ||
      userData.isEdgeHandle ||
      userData.isRotationHandle ||
      userData.isCornerHandle ||
      userData.isCircleTranslate ||
      userData.isCircleScale;
    if (!isHandle) return false;
    const isVisible = (object.visible ?? true) && (target?.visible ?? true);
    if (!isVisible) return false;
    const material = object.material || target?.material;
    if (material && typeof material.opacity === "number" && material.opacity <= 0.05) {
      return false;
    }
    return true;
  };

  // 🔍 2) Check if the click hit a vertex marker first
  //      (they are created with name = "vertex" in createVertex)
  const fullIntersects = raycaster.intersectObjects(
    sceneManager.scene.children,
    true
  );

  if (fullIntersects.some((hit) => isInteractiveHandle(hit.object))) {
    // Click is on a vertex → do NOT run any of the segment/panel/obstacle selection logic
    // DragControls will handle moving that vertex.
    return;
  }

  // 🧠 3) Existing behavior stays the same after that
  if (selectedIndices.segmentIndex === null) {
    // No segment selected → only segment selection
    segmentSelection(
      selectedIndices,
      setSelectedIndices,
      setPolygonDetails,
      raycaster,
      setSolarPanel,
      setShowPanelSettings,
      false
    );
  } else {
    // Segment already selected → panels / obstacles / segments
    const didHitPanel = panelSelection(
      selectedIndices,
      setSelectedIndices,
      setShowPanelSettings,
      raycaster,
      setSolarPanel,
      polygonState,
      appendPanelSelection
    );
    const didHitObstacle = obstacleSelection(selectedIndices, setSelectedIndices, raycaster);
    segmentSelection(
      selectedIndices,
      setSelectedIndices,
      setPolygonDetails,
      raycaster,
      setSolarPanel,
      setShowPanelSettings,
      !didHitPanel && !didHitObstacle
    );
  }
}

// Checks for selection of a segment (polygon shape)
function segmentSelection(
  selectedIndices,
  setSelectedIndices,
  setPolygonDetails,
  raycaster,
  setSolarPanel,
  setShowPanelSettings,
  allowReselectToDetails = false
) {
  // Pick the NEAREST segment surface under the cursor, not the first by index.
  // The currently-selected segment's flat plane often spans underneath smaller
  // raised facets (e.g. a dormer hip like segment C). Iterating by index would
  // hit that underlying plane first and re-select it, so an overlapping facet
  // could be hovered but never clicked. Comparing hit distances selects the
  // facet actually closest to the camera.
  let nearest = null; // { index, distance }
  for (let i = 0; i < POLYGONS.length; i++) {
    const shape = POLYGONS[i]?.shape;
    if (!shape) continue;
    const intersect = raycaster.intersectObject(shape, false); // Check intersection with polygon shape
    if (intersect.length > 0) {
      const distance = intersect[0].distance;
      if (nearest === null || distance < nearest.distance) {
        nearest = { index: i, distance };
      }
    }
  }

  if (nearest === null) return;

  const i = nearest.index;
  if (i !== selectedIndices.segmentIndex) {
    // Preserve the last chosen module in the dropdown while clearing active panel linkage.
    setSolarPanel((prev) => ({ ...(prev || {}), panel_id: null }));
    setShowPanelSettings(false); // Hide panel settings UI
    selectSegment(selectedIndices, i, setSelectedIndices, setPolygonDetails); // Select new segment
  } else if (allowReselectToDetails) {
    setSolarPanel((prev) => ({ ...(prev || {}), panel_id: null }));
    setShowPanelSettings(false);
    setSelectedIndices({
      segmentIndex: i,
      obstacleIndex: null,
      panelIndex: null,
    });
  }
}

// Checks for selection of a panel within a segment
function panelSelection(
  selectedIndices,
  setSelectedIndices,
  setShowPanelSettings,
  raycaster,
  setSolarPanel,
  polygonsState,
  appendPanelSelection = false
) {
  const panels = POLYGONS[selectedIndices.segmentIndex].panels.map((pan) => pan.panel); // Get panel meshes
  const directIntersects = raycaster.intersectObjects(panels, false); // Prefer direct panel-face hits
  let hitPanelMesh = directIntersects[0]?.object ?? null;

  const resolvePanelMesh = (object) => {
    let current = object;
    while (current) {
      if (panels.includes(current)) return current;
      current = current.parent ?? null;
    }
    return null;
  };

  // Fallback: descendant hit-test for cases where only child objects are raycasted.
  if (!hitPanelMesh) {
    const previousLineThreshold = raycaster.params?.Line?.threshold;
    raycaster.params.Line = { threshold: 0.01 };
    const recursiveIntersects = raycaster.intersectObjects(panels, true);
    hitPanelMesh = recursiveIntersects
      .map((hit) => resolvePanelMesh(hit.object))
      .find(Boolean) || null;

    if (previousLineThreshold === undefined) {
      delete raycaster.params.Line;
    } else {
      raycaster.params.Line = { threshold: previousLineThreshold };
    }
  }

  if (hitPanelMesh) {
    const panelIndexToAdd = panels.indexOf(hitPanelMesh); // Get index of intersected panel
    // Set the current solar panel data from state
    const uiPanel =
      polygonsState?.[selectedIndices.segmentIndex]?.panels?.[panelIndexToAdd] || {};
    const polygonPanel =
      POLYGONS?.[selectedIndices.segmentIndex]?.panels?.[panelIndexToAdd] || null;
    const catalogId =
      polygonPanel?.id ??
      uiPanel?.id ??
      null;
    const meshId =
      polygonPanel?.panel?.id ??
      uiPanel?.panel_id ??
      null;
    const panelTransform = polygonPanel?.panel?.userData?.panelTransform || {};
    setSolarPanel({
      ...uiPanel,
      id: catalogId,
      panel_id: meshId,
      tilt: uiPanel?.tilt ?? panelTransform.tilt ?? 0,
      rotation: uiPanel?.rotation ?? panelTransform.rotation ?? 0,
      azimuth: uiPanel?.azimuth ?? panelTransform.azimuth ?? 0,
    });

    selectSinglePanel(
      panelIndexToAdd,
      setSelectedIndices,
      selectedIndices,
      setShowPanelSettings,
      { append: appendPanelSelection }
    );
    return true;
  } else {
    // If no panel intersected, deselect
    setSolarPanel((prev) => ({ ...(prev || {}), panel_id: null }));
    setShowPanelSettings(false);
    deselectPanels(selectedIndices.panelIndex, setSelectedIndices, selectedIndices);
    return false;
  }
}

// Checks for selection of an obstacle (e.g., obstruction line) within a segment
function obstacleSelection(
  selectedIndices,
  setSelectedIndices,
  raycaster
) {
  const { segmentIndex, obstacleIndex, panelIndex } = selectedIndices;
  if (segmentIndex === null || segmentIndex === undefined) return;

  const obstacles = POLYGONS[segmentIndex].obstructions; // Get obstacle meshes

  for (let i = 0; i < obstacles.length; i++) {
    raycaster.params.Line = { threshold: 0.05 }; // Set raycasting precision for lines
    const intersect = raycaster.intersectObject(obstacles[i].mesh, false); // Check intersection with obstacle
    if (intersect.length > 0) {
      const plane = createSegmentPlane(segmentIndex);
      const segIntersect = new THREE.Vector3();
      raycaster.ray.intersectPlane(plane, segIntersect); // Also intersect with shape
      if (i !== obstacleIndex) {
        // ✅ Pass explicit segmentIndex into selectObstacle
        selectObstacle(
          segmentIndex,
          selectedIndices,
          i,
          setSelectedIndices,
          null
        ); // Select new obstacle
        obstacles[i].mesh.userData.originalPosition = segIntersect; // Store original intersection point
        return true;
      } else return true; // Already selected obstacle, do nothing
    }
  }

  // If no obstacle intersected and one was previously selected, deselect it
  if (obstacleIndex !== null) {
    deselectObstacle(obstacles[obstacleIndex]);
    setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: null, panelIndex: panelIndex }); // Clear obstacle index
  }
  return false;
}

// Helper function to return the index of a hovered panel (used for orientation or placement)
export function panelOrientation(selectedIndices, mouse, sceneManager) {
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, sceneManager.camera);
  let panelIndex = null;

  if (selectedIndices.segmentIndex !== null) {
    const panels = POLYGONS[selectedIndices.segmentIndex].panels.map((pan) => pan.panel); // Get panel meshes
    let intersect = raycaster.intersectObjects(panels, false); // Check intersection
    if (intersect.length > 0) {
      panelIndex = panels.indexOf(intersect[0].object); // Get index of the intersected panel
    }
  }

  return panelIndex; // Return the index or null
}
